import { Active, DragEndEvent, DragStartEvent, Over } from '@dnd-kit/core';
import { JobStateEvent } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { uniqBy } from 'lodash-es';
import { runInAction } from 'mobx';
import { useCallback, useRef, useState } from 'react';

import { trackJobAssignmentCancelled } from '~hooks/useAnalytics/trackJobAssignmentCancelled';
import useComplianceCheck, {
  ComplianceCheckResult,
} from '~hooks/useComplianceCheck/useComplianceCheck';
import { Driver } from '~hooks/useDrivers/models';
import { Job } from '~hooks/useJob/models';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  DUMMY_COLUMN_INCREMENTS,
  INCREMENTS_PER_HOUR,
  MINUTES_PER_INCREMENT,
} from '../components/drivers/constants';
import useDriverSchedulerFetch from './useDriverSchedulerFetch';

interface PendingSchedulerAssignment {
  over: Over;
  active: Active;
  complianceResult: ComplianceCheckResult;
  resolvedJobId: string;
  resolvedJobAssigneeId: Nullable<string>;
}

// A job's current assignee, or null while it has none. Compared across the
// compliance dialog's lifetime: a real-time update can reassign the job while
// the warning is open, and confirming then would unassign whoever took it.
function getJobAssigneeId(job: Job): Nullable<string> {
  return job.driver?.id ?? job.vendorJobAssignment?.vendorAccount?.id ?? null;
}

export const useDriverScheduler = () => {
  const { toasterStore, driverSchedulerStore, userStore } = useStores();
  const { assignDriver, assignVendor, unassignDriver, unassignVendor, updateJob } =
    useDriverSchedulerFetch();
  const { check: checkCompliance, reset: resetCompliance } = useComplianceCheck();
  const [unassignedJobs, setUnassignedJobs] = useState<Job[]>([]);
  const companyId = getEffectiveUserCompanyId(userStore);
  // Used to preserve the original order of unassigned jobs
  // Allows us to sort the unassigned jobs by their original order when unassigning a job
  const [originalUnassignedJobs, setOriginalUnassignedJobs] = useState<Job[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [pendingSchedulerAssignment, setPendingSchedulerAssignment] =
    useState<PendingSchedulerAssignment | null>(null);
  const [isComplianceOverrideSubmitting, setIsComplianceOverrideSubmitting] =
    useState(false);
  const assignAndUpdateJobTransactionRef = useRef<
    (
      over: Over,
      active: Active,
      complianceOverride?: boolean,
      resolvedJobId?: string,
    ) => Promise<void>
  >(async () => {});

  const columnIndexToDateString = (
    columnIndex: number,
    startDate: string | undefined,
  ): string => {
    const selectedDate = dayjs.tz(startDate);
    // Subtract the total dummy column increments to calculate the correct time
    const adjustedColumnIndex = Math.max(0, columnIndex - DUMMY_COLUMN_INCREMENTS);
    const hour = Math.floor(adjustedColumnIndex / INCREMENTS_PER_HOUR);
    const minute = (adjustedColumnIndex % INCREMENTS_PER_HOUR) * MINUTES_PER_INCREMENT;
    const date = selectedDate.hour(hour).minute(minute).startOf('minute');
    return date.toISOString();
  };

  const assignAndUpdateJob = async (
    job: Job,
    assigneeId: string,
    newStartDate: string,
    columnIndex: number,
    assignedVendorDidChange: boolean,
    complianceOverride?: boolean,
  ) => {
    const isTargetingDriver =
      driverSchedulerStore.assigneesFilters.assigneeType !== 'vendor';

    // Optimistic update start
    const previousAssignments = { ...driverSchedulerStore.jobAssignments };
    const previousStacks = { ...driverSchedulerStore.jobStacks };
    const previousUnassignedJobs = [...driverSchedulerStore.unassignedJobs];
    const previousAssignedJobs = [...driverSchedulerStore.assignedJobs];

    const newAssignments: Record<string, Nullable<Job>> = {
      ...driverSchedulerStore.jobAssignments,
    };
    const newStacks: Record<string, Job[]> = {
      ...driverSchedulerStore.jobStacks,
    };

    Object.keys(newAssignments).forEach((key) => {
      if (newAssignments[key] && newAssignments[key]?.id === job.id) {
        newAssignments[key] = null;
        if (newStacks[key]) {
          newStacks[key] = newStacks[key].filter((j) => j.id !== job.id);
          if (newStacks[key].length === 0) {
            delete newStacks[key];
          } else {
            newAssignments[key] = newStacks[key][newStacks[key].length - 1];
          }
        }
      }
    });

    const slotKey = `${assigneeId}:${columnIndex}`;
    if (!newStacks[slotKey]) {
      newStacks[slotKey] = [];
    }
    newStacks[slotKey].push(job);
    newAssignments[slotKey] = job;

    runInAction(() => {
      driverSchedulerStore.setJobAssignments(newAssignments, newStacks);
    });

    // Optimistic update end

    try {
      let updatedJob: Job | null = null;

      if (isTargetingDriver) {
        updatedJob = await assignDriver(
          job.id,
          assigneeId,
          companyId,
          complianceOverride,
          newStartDate,
        );
      } else {
        if (assignedVendorDidChange) {
          updatedJob = await assignVendor(
            job.id,
            assigneeId,
            companyId,
            complianceOverride,
          );
        }

        updatedJob = await updateJob(
          job.id,
          { ...job, jobStartAt: dayjs(newStartDate) } as Job,
          companyId,
          true,
        );
      }

      runInAction(() => {
        if (!updatedJob) {
          throw new Error('Updated job is null or undefined');
        }

        // Update the assigned jobs
        driverSchedulerStore.assignedJobs = uniqBy(
          [...driverSchedulerStore.assignedJobs, updatedJob],
          (j) => j.id,
        );

        // Remove the job from unassigned jobs
        driverSchedulerStore.unassignedJobs = driverSchedulerStore.unassignedJobs.filter(
          (unassignedJob) => unassignedJob.id !== job.id,
        );
      });

      toasterStore.removeFirst();
      toasterStore.push(
        alert(t('dispatch.drivers.job_successfully_assigned'), AlertTypes.success),
      );

      return { updatedJob };
    } catch (error) {
      console.error(
        `Failed to assign ${isTargetingDriver ? 'driver' : 'vendor'} and update job:`,
        error,
      );

      // Rollback optimistic update
      runInAction(() => {
        driverSchedulerStore.setJobAssignments(previousAssignments, previousStacks);
        driverSchedulerStore.unassignedJobs = previousUnassignedJobs;
        driverSchedulerStore.assignedJobs = previousAssignedJobs;
      });

      throw error;
    }
  };

  const assignAndUpdateJobTransaction = async (
    over: Over,
    active: Active,
    complianceOverride?: boolean,
    resolvedJobId?: string,
  ) => {
    const isOrderDrag = active.data?.current?.type === 'order';
    const allJobs = uniqBy(
      [...driverSchedulerStore.assignedJobs, ...driverSchedulerStore.unassignedJobs],
      (job: Job) => job.id,
    );
    // Callers pass the job the drag actually resolved to. Re-deriving it from
    // the order would silently retarget the assignment whenever a real-time
    // update reorders the group between drag start and the drop (or the
    // compliance confirm). The order-id lookup below remains for callers that
    // have no resolved job to hand.
    const draggedJob = resolvedJobId
      ? allJobs.find((job: Job) => job.id === resolvedJobId)
      : isOrderDrag
        ? driverSchedulerStore.unassignedOrderGroups.find(
            (group) => group.order.id === active.id,
          )?.jobs[0]
        : allJobs.find((job: Job) => job.id === active.id);

    if (!draggedJob) {
      return;
    }
    const draggedJobId = draggedJob.id;

    const [assigneeId, rawColumnIndex] = String(over.id).split(':');
    const columnIndex = String(Math.max(DUMMY_COLUMN_INCREMENTS, Number(rawColumnIndex)));

    const previousAssignments = { ...driverSchedulerStore.jobAssignments };
    const previousStacks = { ...driverSchedulerStore.jobStacks };
    const previousUnassignedJobs = [...driverSchedulerStore.unassignedJobs];
    const previousAssignedJobs = [...driverSchedulerStore.assignedJobs];
    const newAssignments: Record<string, Nullable<Job>> = {
      ...driverSchedulerStore.jobAssignments,
    };
    const newStacks: Record<string, Job[]> = {
      ...driverSchedulerStore.jobStacks,
    };

    Object.keys(newAssignments).forEach((key) => {
      if (newAssignments[key] && newAssignments[key]?.id === draggedJobId) {
        newAssignments[key] = null;
        if (newStacks[key]) {
          newStacks[key] = newStacks[key].filter((j) => j.id !== draggedJobId);
          if (newStacks[key].length === 0) {
            delete newStacks[key];
          } else {
            newAssignments[key] = newStacks[key][newStacks[key].length - 1];
          }
        }
      }
    });

    const slotKey = `${assigneeId}:${columnIndex}`;
    if (!newStacks[slotKey]) {
      newStacks[slotKey] = [];
    }
    newStacks[slotKey].push(draggedJob);
    newAssignments[slotKey] = draggedJob;

    runInAction(() => {
      driverSchedulerStore.jobAssignments = newAssignments;
      driverSchedulerStore.jobStacks = newStacks;
      driverSchedulerStore.unassignedJobs = driverSchedulerStore.unassignedJobs.filter(
        (job) => job.id !== draggedJobId,
      );
      driverSchedulerStore.assignedJobs = uniqBy(
        [...driverSchedulerStore.assignedJobs, draggedJob],
        (job: Job) => job.id,
      );
    });

    const selectedStartDate = driverSchedulerStore.dateFilters.startDate;
    const newStartDate = columnIndexToDateString(Number(columnIndex), selectedStartDate);

    const isTargetingDriver =
      driverSchedulerStore.assigneesFilters.assigneeType !== 'vendor';

    try {
      const isJobAssigned = Boolean(
        draggedJob.driver?.id ?? draggedJob.vendorJobAssignment?.vendorAccount?.id,
      );

      const shouldUnassignDriver =
        isJobAssigned && isTargetingDriver && draggedJob.driver?.id !== assigneeId;

      const assignedVendorDidChange =
        !isTargetingDriver &&
        draggedJob.vendorJobAssignment?.vendorAccount?.id !== assigneeId;
      const shouldUnassignVendor = isJobAssigned && assignedVendorDidChange;

      if (shouldUnassignDriver) {
        await unassignDriver(draggedJob.id, companyId);
      } else if (shouldUnassignVendor) {
        await unassignVendor(draggedJob.id, companyId);
      }

      await assignAndUpdateJob(
        draggedJob,
        assigneeId,
        newStartDate,
        Number(columnIndex),
        assignedVendorDidChange,
        complianceOverride,
      );
    } catch (error) {
      console.error(
        `Failed to assign ${isTargetingDriver ? 'driver' : 'vendor'} and update job:`,
        error,
      );
      // Rollback the optimistic update if there's an error
      runInAction(() => {
        driverSchedulerStore.jobAssignments = previousAssignments;
        driverSchedulerStore.jobStacks = previousStacks;
        driverSchedulerStore.unassignedJobs = previousUnassignedJobs;
        driverSchedulerStore.assignedJobs = previousAssignedJobs;
      });
    }
  };
  assignAndUpdateJobTransactionRef.current = assignAndUpdateJobTransaction;

  const unassignTransaction = async (active: Active) => {
    const assignedJob = driverSchedulerStore.assignedJobs.find(
      (job) => job.id === active.id,
    );

    if (!assignedJob) {
      return;
    }

    // Optimistically update unassignedJobs and jobAssignments
    const previousAssignments = { ...driverSchedulerStore.jobAssignments };
    const previousStacks = { ...driverSchedulerStore.jobStacks };
    const previousUnassignedJobs = [...driverSchedulerStore.unassignedJobs];
    const previousAssignedJobs = [...driverSchedulerStore.assignedJobs];

    const newAssignments: Record<string, Nullable<Job>> = { ...previousAssignments };
    const newStacks: Record<string, Job[]> = { ...previousStacks };
    const newAssignedJobs = previousAssignedJobs.filter((job) => job.id !== active.id);

    Object.keys(newAssignments).forEach((key) => {
      if (newAssignments[key] && newAssignments[key]?.id === active.id) {
        newAssignments[key] = null;
        if (newStacks[key]) {
          newStacks[key] = newStacks[key].filter((j) => j.id !== active.id);
          if (newStacks[key].length === 0) {
            delete newStacks[key];
          } else {
            newAssignments[key] = newStacks[key][newStacks[key].length - 1];
          }
        }
      }
    });

    runInAction(() => {
      driverSchedulerStore.jobAssignments = newAssignments;
      driverSchedulerStore.jobStacks = newStacks;
      driverSchedulerStore.assignedJobs = newAssignedJobs;
      driverSchedulerStore.unassignedJobs = uniqBy(
        [...driverSchedulerStore.unassignedJobs, assignedJob],
        (job: Job) => job.id,
      ).sort(
        (a, b) =>
          originalUnassignedJobs.findIndex((job) => job.id === a.id) -
          originalUnassignedJobs.findIndex((job) => job.id === b.id),
      );
    });

    const isTargetingDriver =
      driverSchedulerStore.assigneesFilters.assigneeType !== 'vendor';

    // Perform the actual unassign call
    try {
      if (isTargetingDriver) {
        await unassignDriver(assignedJob.id, companyId);
      } else {
        await unassignVendor(assignedJob.id, companyId);
      }

      trackJobAssignmentCancelled({
        action: JobStateEvent.UNASSIGN,
        jobIds: [assignedJob.id],
        source: 'scheduler_drag',
      });
      toasterStore.removeFirst();
      toasterStore.push(
        alert(t('dispatch.drivers.job_successfully_unassigned'), AlertTypes.success),
      );
    } catch (error) {
      console.error(
        `Failed to unassign ${isTargetingDriver ? 'driver' : 'vendor'}:`,
        error,
      );
      // Rollback the optimistic update if there's an error
      runInAction(() => {
        driverSchedulerStore.jobAssignments = previousAssignments;
        driverSchedulerStore.jobStacks = previousStacks;
        driverSchedulerStore.unassignedJobs = previousUnassignedJobs;
        driverSchedulerStore.assignedJobs = previousAssignedJobs;
      });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    if (active.data?.current?.type === 'order') {
      driverSchedulerStore.setDraggedOrder(String(active.id));
      return;
    }

    const unassignedJob = driverSchedulerStore.unassignedJobs.find(
      (job) => job.id === active.id,
    );
    const assignedJob = driverSchedulerStore.assignedJobs.find(
      (job) => job.id === active.id,
    );

    const draggedJob = assignedJob || unassignedJob;
    driverSchedulerStore.setDraggedJob(draggedJob || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      driverSchedulerStore.setDraggedJob(null);
      return;
    }

    const draggedJob = driverSchedulerStore.draggedJob;

    if (!draggedJob) {
      driverSchedulerStore.setDraggedJob(null);
      return;
    }

    if (over.id === 'jobs-column') {
      const isJobAssigned = Boolean(
        draggedJob.driver?.id ?? draggedJob.vendorJobAssignment?.vendorAccount?.id,
      );

      // Prevent dragging back to unassigned if job doesn't have 'unassign' state event
      const canUnassign = draggedJob.stateEvents.includes(JobStateEvent.UNASSIGN);

      if (!canUnassign && isJobAssigned) {
        driverSchedulerStore.setDraggedJob(null);
        return;
      }

      if (isJobAssigned) {
        await unassignTransaction(active);
      }
    } else {
      const [assigneeId] = String(over.id).split(':');
      const jobAssigneeId =
        draggedJob.driver?.id ?? draggedJob.vendorJobAssignment?.vendorAccount?.id;
      const isSwitchingAssignee = jobAssigneeId && jobAssigneeId !== assigneeId;

      // Prevent dragging back to unassigned if job doesn't have 'unassign' state event
      const canUnassign = draggedJob.stateEvents.includes(JobStateEvent.UNASSIGN);

      if (!canUnassign && isSwitchingAssignee) {
        driverSchedulerStore.setDraggedJob(null);
        return;
      }

      const isTargetingDriver =
        driverSchedulerStore.assigneesFilters.assigneeType !== 'vendor';
      const isAssigneeChanging = !jobAssigneeId || jobAssigneeId !== assigneeId;

      if (isAssigneeChanging) {
        const checkResult = await checkCompliance(
          companyId,
          isTargetingDriver ? undefined : assigneeId,
          isTargetingDriver ? assigneeId : undefined,
        );

        if (checkResult.hasIssues) {
          setPendingSchedulerAssignment({
            over,
            active,
            complianceResult: checkResult,
            resolvedJobId: draggedJob.id,
            resolvedJobAssigneeId: getJobAssigneeId(draggedJob),
          });
          driverSchedulerStore.setDraggedJob(null);
          return;
        }
      }

      await assignAndUpdateJobTransaction(over, active, undefined, draggedJob.id);
    }

    driverSchedulerStore.setDraggedJob(null);
  };

  const handleDragCancel = () => {
    driverSchedulerStore.setDraggedJob(null);
  };

  const confirmSchedulerAssignment = useCallback(async () => {
    if (pendingSchedulerAssignment) {
      const { over, active, resolvedJobId, resolvedJobAssigneeId } =
        pendingSchedulerAssignment;
      const currentJob = [
        ...driverSchedulerStore.assignedJobs,
        ...driverSchedulerStore.unassignedJobs,
      ].find((job: Job) => job.id === resolvedJobId);
      // Existing is not enough: a job reassigned since the warning opened would
      // have the concurrent assignee stripped by this override.
      const isJobStillAvailable =
        !!currentJob && getJobAssigneeId(currentJob) === resolvedJobAssigneeId;

      if (!isJobStillAvailable) {
        toasterStore.push(
          alert(
            t('error_messages.jobs.dragged_job_no_longer_available'),
            AlertTypes.error,
          ),
        );

        return;
      }

      setIsComplianceOverrideSubmitting(true);
      try {
        await assignAndUpdateJobTransactionRef.current(over, active, true, resolvedJobId);
      } finally {
        setIsComplianceOverrideSubmitting(false);
        setPendingSchedulerAssignment(null);
        resetCompliance();
      }
    }
  }, [pendingSchedulerAssignment, resetCompliance]);

  const cancelSchedulerAssignment = useCallback(() => {
    setPendingSchedulerAssignment(null);
    resetCompliance();
  }, [resetCompliance]);

  return {
    unassignedJobs,
    setUnassignedJobs,
    originalUnassignedJobs,
    setOriginalUnassignedJobs,
    drivers,
    setDrivers,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    pendingSchedulerAssignment,
    isComplianceOverrideSubmitting,
    confirmSchedulerAssignment,
    cancelSchedulerAssignment,
  };
};
